import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Bell, X, AlertTriangle, Wallet, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";
import { fmtDate } from "@/components/ui-kit";

const KIND_ICON = {
  collection_overdue: Wallet,
  expiry: AlertTriangle,
  visit_followup: MapPin,
  scheduled_wa: MessageCircle,
};
const KIND_COLOR = {
  collection_overdue: "text-amber-600 bg-amber-50 dark:bg-amber-950/40",
  expiry: "text-red-600 bg-red-50 dark:bg-red-950/40",
  visit_followup: "text-blue-600 bg-blue-50 dark:bg-blue-950/40",
  scheduled_wa: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40",
};

export default function NotificationBell() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setList(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000); // poll every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = list.filter(n => !n.read).length;

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    await load();
  };
  const markAllRead = async () => {
    await api.put("/notifications/read-all");
    await load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className="relative p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
      >
        <Bell className="w-5 h-5"/>
        {unread > 0 && (
          <span className="absolute top-1 end-1 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center px-1">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute end-0 top-full mt-2 w-96 max-w-[90vw] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-xl z-40">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Manrope" }}>Notifications</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-blue-700 dark:text-blue-400 hover:underline">Mark all read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {list.length > 0 ? list.map(n => {
              const Icon = KIND_ICON[n.kind] || Bell;
              return (
                <a key={n.id} href={n.link || "#"} onClick={() => !n.read && markRead(n.id)}
                  className={`flex items-start gap-3 p-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${!n.read ? "bg-blue-50/40 dark:bg-blue-950/20" : ""}`}>
                  <div className={`w-8 h-8 rounded-md grid place-items-center flex-shrink-0 ${KIND_COLOR[n.kind] || "bg-slate-100 text-slate-600 dark:bg-slate-800"}`}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${!n.read ? "font-bold text-slate-900 dark:text-white" : "font-medium text-slate-700 dark:text-slate-300"}`}>{n.title}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{fmtDate(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-1"/>}
                </a>
              );
            }) : (
              <div className="p-8 text-center text-sm text-slate-400">
                <CheckCircle2 className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
                You're all caught up!
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
